/* ──────────────────────────────────────────────────────────────────────
 * LeadActionsPanel  (file path kept as ContactLogPanel.tsx for import
 * stability — every existing surface imports `from "./ContactLogPanel"`)
 *
 * Top-level composer for the redesigned Lead Actions surface. Stacks the
 * three focused workflow cards described in the enterprise CRM spec:
 *
 *   1. LeadManagementCard      — Status / Priority / Assignee / VIP
 *   2. QuickActionsCard        — Log Call · Note · Email · Meeting · Schedule
 *   3. FollowUpManagementCard  — Amber-styled task list + per-row controls
 *
 * (Section 4 of the spec — Activity Timeline — already lives further down
 * the page as <LeadActivityTimeline />; not part of this composer because
 * it has its own positioning requirements on each surface.)
 *
 * The composer keeps the original prop signature so callers don't need
 * to change:  <ContactLogPanel lead={lead} teamUsers={teamUsers} … />
 * ──────────────────────────────────────────────────────────────────── */

import React, { useEffect, useState, useCallback } from "react";
import { Box, Stack, Paper } from "@mui/material";
import LeadManagementCard from "./LeadManagementCard";
import QuickActionsCard from "./QuickActionsCard";
import FollowUpManagementCard from "./FollowUpManagementCard";
import { type LeadThread } from "./leadApi";
import { fetchLeadTasks, type Task } from "../Tasks/taskApi";

const SURFACE = {
  card:       "#ffffff",
  cardBorder: "1px solid #eef0f4",
  cardShadow: "0 1px 2px rgba(13,27,42,0.04), 0 4px 16px rgba(13,27,42,0.04)",
  cardRadius: 2,
} as const;

interface TeamUserLike {
  id: number;
  display_name?: string | null;
  username?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  role_name?: string | null;
}

interface Props {
  lead: LeadThread;
  teamUsers: TeamUserLike[];
  /** Compact = drawer variant (slimmer paddings, no individual card borders). */
  variant?: "card" | "compact";
  onLogged?: () => void;
  /** Optional task list — if provided, the composer skips its own fetch
   *  and renders FollowUpManagementCard against the parent's data so the
   *  full-page surface and the drawer don't double-fetch the same rows. */
  tasks?: Task[];
  /** When the parent owns task state, this fires after any in-card
   *  mutation so the parent can refetch. */
  onTasksChanged?: () => void;
  onOpenTask?: (task: Task) => void;
  /** Read-only historical mode — the current user used to own this lead
   *  but no longer does. All status / priority / VIP / quick-action /
   *  follow-up controls disable. Timeline + customer info remain visible
   *  upstream in <LeadDetailPage />. */
  readOnly?: boolean;
  /** Lock the assignee dropdown specifically — true for every non-admin,
   *  even the current owner. Only Admin / Super Admin can reassign. */
  assigneeLocked?: boolean;
}

export default function LeadActionsPanel({
  lead, teamUsers, variant = "card", onLogged, tasks, onTasksChanged, onOpenTask,
  readOnly = false, assigneeLocked = false,
}: Props) {
  // Local fallback task state — only used when the parent doesn't pass
  // a `tasks` array. The slide-over drawer in particular doesn't manage
  // tasks itself, so we fetch them here to keep the FollowUpManagement
  // card self-sufficient.
  const [localTasks, setLocalTasks] = useState<Task[]>([]);

  const loadTasks = useCallback(async () => {
    try {
      const t = await fetchLeadTasks(lead.id);
      setLocalTasks(t);
    } catch { /* silent — empty array renders the empty state */ }
  }, [lead.id]);

  useEffect(() => {
    if (tasks === undefined) loadTasks();
  }, [tasks, loadTasks]);

  const effectiveTasks = tasks ?? localTasks;

  const handleAnyChanged = () => {
    onLogged?.();
    if (tasks === undefined) loadTasks();
    onTasksChanged?.();
  };

  /* The drawer "compact" variant flattens the three sections into a
     single Paper so the slide-over stays visually contained; the
     full-page "card" variant gives each section its own surface for
     scannability in the wider rail. */
  if (variant === "compact") {
    return (
      <Stack gap={1.6}>
        <LeadManagementCard lead={lead} teamUsers={teamUsers} onChanged={handleAnyChanged} readOnly={readOnly} assigneeLocked={assigneeLocked} />
        <Box sx={{ height: 1, bgcolor: "#eef0f4", my: 0.2 }} />
        <QuickActionsCard lead={lead} teamUsers={teamUsers} onChanged={handleAnyChanged} readOnly={readOnly} />
        <Box sx={{ height: 1, bgcolor: "#eef0f4", my: 0.2 }} />
        <FollowUpManagementCard tasks={effectiveTasks} onChanged={handleAnyChanged} onOpenTask={onOpenTask} readOnly={readOnly} />
      </Stack>
    );
  }

  return (
    <Stack gap={1.6}>
      <Paper elevation={0} sx={{ p: 2, ...SURFACE, border: SURFACE.cardBorder }}>
        <LeadManagementCard lead={lead} teamUsers={teamUsers} onChanged={handleAnyChanged} readOnly={readOnly} assigneeLocked={assigneeLocked} />
      </Paper>
      <Paper elevation={0} sx={{ p: 2, ...SURFACE, border: SURFACE.cardBorder }}>
        <QuickActionsCard lead={lead} teamUsers={teamUsers} onChanged={handleAnyChanged} readOnly={readOnly} />
      </Paper>
      <Paper elevation={0} sx={{ p: 2, ...SURFACE, border: SURFACE.cardBorder }}>
        <FollowUpManagementCard tasks={effectiveTasks} onChanged={handleAnyChanged} onOpenTask={onOpenTask} readOnly={readOnly} />
      </Paper>
    </Stack>
  );
}
